import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Canonical brand-band header for `.sai-dialog-shell` MatDialogs.
 * Renders the sticky `.header.bg-primary` block (icon / title / subtitle / close)
 * so every dialog shares one markup contract — see `docs/PATTERNS.md` §7.
 *
 * Projection slots (optional; prefer inputs for plain strings):
 * - `[saiDialogHeaderIcon]` — custom icon inside `.header-icon`
 * - `[saiDialogHeaderTitle]` — composed title inside `.header-title`
 * - `[saiDialogHeaderSubtitle]` — composed subtitle (add `class="header-subtitle"` on the host)
 * - `[saiDialogHeaderActions]` — trailing actions before the close button
 */
@Component({
    selector: 'app-sai-dialog-header',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [MatButtonModule, MatDialogModule, MatIconModule, MatTooltipModule],
    templateUrl: './sai-dialog-header.component.html',
    styleUrls: ['./sai-dialog-header.component.scss'],
})
export class SaiDialogHeaderComponent {
    private readonly _transloco = inject(TranslocoService);

    /** Heroicons / Fuse `svgIcon` name, e.g. `heroicons_outline:cpu-chip`. */
    @Input() icon = '';

    /** Material font ligature when not using an svgIcon, e.g. `table_chart`. */
    @Input() iconLigature = '';

    /** Plain title text. Prefer projecting `[saiDialogHeaderTitle]` for composed titles. */
    @Input() title = '';

    /** Plain subtitle text. Prefer projecting `[saiDialogHeaderSubtitle]` for composed subtitles. */
    @Input() subtitle = '';

    /** Show the circular close affordance (default true). */
    @Input() showClose = true;

    /** Tooltip on the close button; defaults to i18n `Close`. */
    @Input() closeTooltip = '';

    /** Aria-label on the close button; defaults to `closeTooltip` or i18n `Close`. */
    @Input() closeAriaLabel = '';

    /**
     * When true, the close button also carries `[mat-dialog-close]="closeResult"`.
     * Use for dialogs that close via MatDialogClose instead of a custom `(closed)` handler.
     */
    @Input() closeWithDialog = false;

    /** Value passed to `mat-dialog-close` when `closeWithDialog` is true. */
    @Input() closeResult: unknown;

    /** Emitted when the close button is clicked (always; even when `closeWithDialog` is true). */
    @Output() readonly closed = new EventEmitter<void>();

    get resolvedCloseTooltip(): string {
        return this.closeTooltip || this._transloco.translate('Close');
    }

    get resolvedCloseAriaLabel(): string {
        return this.closeAriaLabel || this.resolvedCloseTooltip;
    }

    onCloseClick(): void {
        this.closed.emit();
    }
}
